/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import {load, register} from '@shopify/theme-sections';
import * as product from '@shopify/theme-product';
import {ProductForm , getUrlWithVariant} from '@shopify/theme-product-form';
import {formatMoney} from '@shopify/theme-currency';
import Swiper, { Navigation, Scrollbar, A11y } from 'swiper';
//import Drift from 'drift-zoom';

import { initializeAccordion } from 'snippets/_containers/accordion/accordion'; // Importing the accordion script

// Style includes
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

const selectors = {
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',
    originalSelectorId: '[data-product-select]',
    priceWrapper: '[data-price-wrapper]',
    productImageWrapper: '[data-product-image-wrapper]',
    productFeaturedImage: '[data-product-featured-image]',
    productJson: '[data-product-page]',
    productPrice: '[data-product-price]',
    productThumbnailsSlider: 'carousel--product-thumbnails',
    productFeatureSlider: 'carousel--featured-product',
    productSlides: '.swiper-wrapper .swiper-slide',
    productThumbs: '[data-product-single-thumbnail]',
    productForm: '[data-product-form]',
    radioOptionSelector: '[data-radio-option-selector]',
    singleOptionSelector: '[data-single-option-selector]',
    productMediaWrapper: '[data-product-single-media-wrapper]',
    productMediaTypeVideo: '[data-product-media-type-video]',
    productMediaTypeModel: '[data-product-media-type-model]',
    variantOptionLabels: '.swatch__option label',
    formIncrement: '.form__increment',
    quantity: '#Quantity',
    productSku: '.product__sku-number',
    productModelContent: '.product__model-size',
    productSwiperThumbs: null,
    productSwiperFeature: null,
    productSwiperFeatureSets: {},
    productSwiperThumbnailSets: {}
};

const cssClasses = {
    activeThumbnail: 'active-thumbnail',
    hide: 'hide',
};

document.addEventListener('DOMContentLoaded', () => {
    /**
     * Product section constructor. Runs on page load as well as Theme Editor
     * `section:load` events.
     * @param {string} container - selector for the section container DOM element
    */
    register('product', {
        async onLoad() {
            const productFormElement = document.querySelector(selectors.productForm);

            // Stop parsing if we don't have the product json script tag when loading
            // section in the Theme Editor
            if (!this.container.querySelector(selectors.productJson)?.innerHTML) {
                return;
            }

            this.product = await product.getProductJson(
                productFormElement.dataset.productHandle,
            );

            this.productForm = new ProductForm(productFormElement, this.product, {
                onOptionChange: this.onFormOptionChange.bind(this),
                onQuantityChange: this.onFormQuantityChange.bind(this)
            });

            this.currentVariant = this.productForm.variant();
            this.featuredImage = document.querySelector(selectors.productFeaturedImage);
            this.swiperSlides = this.featuredImage.querySelectorAll(selectors.productSlides);

            this.initProductCarousel();
            this.initProductVideo();
            this.initVariantTitles();
            this.initVariantHoverWatch();
            this.initStickyCart();

            this.updateVariantPickupAvailability(this.container, this.currentVariant);
            // this.initProductZoom();
            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages({variant: this.currentVariant, target: this.container});
            }
        },

        onFormOptionChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            if (currentVariant.variant) {
                this.currentVariant = currentVariant.variant;
            }

            this.updatePDPInformation(currentVariant);
            this.updateProductPrices(currentVariant);
            this.updateURLwithVariant(currentVariant);

            if (this.featuredImage && this.swiperSlides.length > 1) {
                this.updateImages(currentVariant);
            }
        },

        onFormQuantityChange(event) {
            const currentVariant = {variant: event.dataset.variant, target: this.container};
            this.updateProductPrices(currentVariant);
        },

        initVariantTitles() {
            this.changeVariantTitles(this.currentVariant);
        },

        updateURLwithVariant(event) {
            const variant = event.variant;

            if (!variant) return;

            const url = getUrlWithVariant(window.location.href, variant.id);
            window.history.replaceState({ path: url }, '', url);
        },


        updatePDPInformation(event) {
            this.updateMasterSelect(event);
            this.updateAddToCartState(event);
            this.changeVariantTitles(event);
            this.changeProductCarousel(event);
            this.changeVariantSku(event);
            this.updateVariantPickupAvailability(this.container, event.variant);
        },

        updateMasterSelect(event) {
            const variant = event.variant;

            if (!variant) return;

            this.container.querySelector(selectors.originalSelectorId).value = variant.id;
        },

        updateAddToCartState(event) {
            const variant = event.variant;

            if (variant) {
                this.container.querySelector(selectors.priceWrapper).classList.remove(cssClasses.hide);
            } else {
                this.container.querySelector(selectors.addToCart).disabled = true;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.unavailable;
                this.container.querySelector(selectors.priceWrapper).classList.add(cssClasses.hide);
                return;
            }

            if (variant.available) {
                this.container.querySelector(selectors.addToCart).disabled = false;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.addToCart;
            } else {
                this.container.querySelector(selectors.addToCart).disabled = true;
                this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.soldOut;
            }
        },

        changeVariantTitles() {
            document.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach((swatch__option) => {
                const container = swatch__option.closest('.form__element');
                const subtitle = container.querySelector('.swatch__title .swatch__subtitle');
                subtitle.textContent = swatch__option.value;
            });
        },

        changeVariantSku(event) {
            const variant = event.variant;
            const sku = document.querySelector(selectors.productSku);

            if(sku) {
                sku.innerHTML = variant.sku;
            }

        },

        tempChangeVariantTitle(event) {
            const target = event.currentTarget;
            const newLabel = target.textContent;
            const container = target.closest('.form__element');
            const subtitle = container.querySelector('.swatch__title .swatch__subtitle');
            subtitle.textContent = newLabel;
        },

        initVariantHoverWatch() {
            const changeVariantTitles = this.changeVariantTitles;
            const tempChangeVariantTitle = this.tempChangeVariantTitle;
            document.querySelectorAll('.swatch__option label.form__option-label').forEach((swatch__option) => {
                swatch__option.addEventListener('mouseenter', tempChangeVariantTitle);
                swatch__option.addEventListener('mouseleave', changeVariantTitles);
            });
        },

        updateImages(event) {
            const variant = event.variant;
            if (variant != null && typeof variant.featured_media != 'undefined') {
                const imageId = this.id + '-' + variant.featured_media.id;
                this.swapImage(imageId);
            }
        },

        updateProductPrices(evt) {
            const variant = evt.variant;
            if (!variant) {
                return false;
            }
            const comparePrice = this.container.querySelector(selectors.comparePrice);
            const comparePriceText = this.container.querySelector(selectors.comparePriceText);
            const quantity = parseInt(this.container.querySelector(selectors.quantity).value);

            this.container.querySelector(selectors.productPrice).innerHTML = formatMoney(variant.price, theme.settingCurrencyFormat);

            const priceWithQuantity = variant.price * quantity;
            document.querySelector('#addToCartPricing').innerHTML = formatMoney(priceWithQuantity, theme.moneyWithoutCurrency);

            if (variant.compare_at_price > variant.price) {
                comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.settingCurrencyFormat);
                comparePrice.classList.remove(cssClasses.hide);
                comparePriceText.classList.remove(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.add('price__original--sale');
            } else if(comparePrice) {
                comparePrice.innerHTML = '';
                comparePrice.classList.add(cssClasses.hide);
                comparePriceText.classList.add(cssClasses.hide);
                this.container.querySelector(selectors.productPrice).classList.remove('price__original--sale');
            }
        },

        disableVariants(target, valuesToEnable) {
            // Loop through all the Option Selectors and check which one is a swatch selector
            // The target is usually the main product container
            const optionSelector1 = target.querySelectorAll('[data-index="option1"]');
            const optionSelector2 = target.querySelectorAll('[data-index="option2"]');
            const optionSelector3 = target.querySelectorAll('[data-index="option3"]');
            let is_swatch_selector1 = false;
            let is_swatch_selector2 = false;
            let is_swatch_selector3 = false;

            if (optionSelector1.length > 0 && optionSelector1[0].getAttribute('type') == 'radio') {
                is_swatch_selector1 = true;
            }

            if (optionSelector2.length && optionSelector2[0].getAttribute('type') == 'radio') {
                is_swatch_selector2 = true;
            }

            if (optionSelector3.length && optionSelector3[0].getAttribute('type') == 'radio') {
                is_swatch_selector3 = true;
            }

            const option1 = target.querySelectorAll('input[data-index="option1"]');
            const option2 = target.querySelectorAll('input[data-index="option2"]');
            const option3 = target.querySelectorAll('input[data-index="option3"]');

            // If there are 3 option selectors
            if (optionSelector3.length > 0) {

                // If the third option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 2.
                if (is_swatch_selector3 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option3.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option3.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1 and 3.
                } else if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2 and 3.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }

            // If there are 2 option selectors
            } else if (optionSelector2.length > 0) {

                // If the second option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 1.
                if (is_swatch_selector2 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                    });

                    option2.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option2.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });

                // If the first option selector is a swatch selector, the available/unavailable state only shows up on this selector based on selected options from Selector 2.
                } else if (is_swatch_selector1 == true) {

                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            } else {
                if (is_swatch_selector1 == true) {
                    option1.forEach((option) => {
                        const input = option;
                        input.nextElementSibling.classList.remove('swatch--disabled');
                        if (!valuesToEnable.option1.includes(input.value)) {
                            input.nextElementSibling.classList.add('swatch--disabled');
                        }
                    });
                }
            }
        },

        updateVariantPickupAvailability(target, variant) {
            const container = document.querySelector('[data-store-availability-container]');
        
            if (!container) {
                return;
            }
        
            if (variant) {
                const sectionId = 'pickup-availability';
                const url = `${window.Shopify.routes.root}variants/${variant.id}/?section_id=${sectionId}`;
        
                fetch(url)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`Failed to fetch pickup availability: ${response.statusText}`);
                        }
                        return response.text();
                    })
                    .then(text => {
                        const parser = new DOMParser();
                        const pickupAvailabilityHTML = parser
                            .parseFromString(text, 'text/html')
                            .querySelector('.shopify-section');
        
                        container.innerHTML = ""; // Clear container before appending
                        if (pickupAvailabilityHTML) {
                            container.appendChild(pickupAvailabilityHTML);

                            // Re-initialize the accordion script
                            initializeAccordion();
                        }
                    })
                    .catch(error => {
                        console.error(`Error loading pickup availability: ${error.message}`);
                        container.innerHTML = ""; // Clear container on error
                    });
            } else {
                container.innerHTML = ""; // Clear container if variant is null
            }
        },

        initStickyCart() {
            const options = {
                rootMargin: '100px 0px 0px 0px',
                threshold: [0,0.01,1]
            };
            const target = document.querySelector('#productStickyContainer');
            const variantSelect = target.previousElementSibling;
            const observer = new IntersectionObserver(this.showHideStickyCart, options);

            // Mobile cart button
            document.querySelector('#addToCartSticky').addEventListener('click', () => {
                setTimeout(function () {
                    variantSelect.scrollIntoView({behavior: 'smooth'});
                },2);
            });

            observer.observe(target);
        },

        showHideStickyCart(entries, observer) {
            let entry = entries[0];
            if (entry.intersectionRatio >= 1) {
                entry.target.classList.remove('product__sticky--show');
            } else if (entry.intersectionRatio <= 0 && entry.boundingClientRect.y <= 0) {
                entry.target.classList.add('product__sticky--show');
                entry.target.classList.remove('product__sticky--hide');
            } else {
                entry.target.classList.add('product__sticky--hide');
            }
        },

        initProductVideo: function() {
            const sectionId = this.id;
            const videoContainers = document.querySelectorAll(selectors.productMediaTypeVideo, this.container);

            videoContainers.forEach((videoContainer) => {
                ProductVideo.init(videoContainer, sectionId);
            });
        },

        // initProductZoom: function() {
        //     if (matchMedia('(pointer:fine)').matches && window.innerWidth> 1023) {
        //         const driftImgs = document.querySelectorAll('.js--image-zoom');
        //         const pane = document.querySelector('.product__feature-image');

        //         driftImgs.forEach((image) => {

        //             // Documentation link for reference: https://github.com/imgix/drift#basic-usage
        //             new Drift(image, {
        //                 paneContainer: pane,
        //                 inlinePane: false,
        //                 handleTouch: false,
        //                 injectBaseStyles: true,
        //                 hoverDelay: 200,
        //             });
        //         });
        //     }
        // },

        initProductCarousel() {
            let self = this;
            // -------------------------------------
            // Carousel Products
            // -------------------------------------
            const productFeatureSwiper = new Swiper('.'+selectors.productFeatureSlider+'.current-variant', {
                // Optional parameters
                modules: [Navigation, A11y, Scrollbar],
                slidesPerView: 1.1,
                spaceBetween: 5,

                // // Navigation arrows
                scrollbar: {
                    el: '.current-variant .swiper-scrollbar',
                    draggable: true,
                },
                on: {
                    init: function() {
                        toggleInnerFocus(this);
                    }
                }
            });

            // The "on" event is proper to the Swiper JS library.
            productFeatureSwiper.on('slideChange ', function () {
                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const previousSlideIndex = productFeatureSwiper.previousIndex,
                    currentSlideIndex = productFeatureSwiper.activeIndex;

                // If current slide has video, trigger "mediaVisible" event to play the video. Then if the previous slide has video, trigger the "mediaHidden" event to pause it.
                const currentMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+currentSlideIndex+'"] .product-single__media-wrapper'),
                    previousMedia = document.querySelector('.product__feature-image .swiper-slide[data-index="'+previousSlideIndex+'"] .product-single__media-wrapper');

                previousMedia.dispatchEvent(new Event('mediaHidden'));
                currentMedia.dispatchEvent(new Event('mediaVisible'));
                toggleInnerFocus(this);
            });

            productFeatureSwiper.slides.each((slide, index) => {
                slide.addEventListener('keydown', function(e) {
                    if (e.key === 'Escape') {
                        // productThumbnailSwiper.slides.each((thumbnailSlide) => {
                        //     thumbnailSlide.setAttribute('tabindex', '0');
                        // });
                        // productThumbnailSwiper.slides[index].focus();
                    }
                });
            });

            // productThumbnailSwiper.slides.each((slide, index) => {
            //     slide.addEventListener('focus', function(e) {
            //         const slideIndex = slide.getAttribute('data-index');
            //         self.slideToSlide(productThumbnailSwiper, slideIndex, true);
            //         self.slideToSlide(productFeatureSwiper, slideIndex, true);
            //         productThumbnailSwiper.slides.each(function(thumbnailSlide) {
            //             thumbnailSlide.setAttribute('tabindex', '0');
            //         });
            //     });
            //     slide.addEventListener('keydown', (e) => {
            //         if (e.key === 'Enter') {
            //             productThumbnailSwiper.slides.each(function(thumbnailSlide) {
            //                 thumbnailSlide.setAttribute('tabindex', '-1');
            //             });
            //             slide.setAttribute('tabindex', '0');
            //             const slideIndex = parseInt(slide.getAttribute('data-index'));
            //             productFeatureSwiper.slides[slideIndex].focus();
            //         }
            //     });
            // });

            selectors.productSwiperFeature = productFeatureSwiper;
        },

        changeProductCarousel(event) {
            let featuredMediaId;

            if (event.variant && event.variant.featured_media) {
                featuredMediaId = event.variant.featured_media.id;

            } else {
                featuredMediaId = "placeholder";
            }

            let carousels = event.target.querySelectorAll('.carousel--featured-product[data-product-featured-image]');
            let singleCarousels = event.target.querySelector('.carousel--one-image');

            if(carousels.length != 0 && singleCarousels == null) {
                carousels.forEach(function(carousel) {
                    carousel.classList.remove('current-variant');
                });

                let newCarousel = event.target.querySelector('.carousel--featured-product[data-product-featured-image="'+featuredMediaId+'"]');

                if(newCarousel) {
                    newCarousel.classList.add('current-variant');
                }

                // Disable old carousel
                selectors.productSwiperFeature.disable();

                // Enable or initialize new carousel
                if (selectors.productSwiperFeatureSets[featuredMediaId]) {
                    selectors.productSwiperFeatureSets[featuredMediaId].enable();
                } else {
                    this.initProductCarousel();
                }
            }
        },


        slideToSlide(slider, slideIndex, transition) {
            if (transition) {
                slider.slideTo(slideIndex);
            } else {
                slider.slideTo(slideIndex, 0);
            }
        },

        // triggering change image when using with Slider
        swapImage(imageId) {
            const newImageId = imageId;

            // If "imageId" is not defined in the function parameter, find it by the current product image
            if (typeof newImageId === 'undefined') {
                newImageId = document.querySelector(
                    `${selectors.productImageWrapper}:not('.${cssClasses.hide}')`
                ).dataset.imageId;
            }
        },

        /**
         * Event callback for Theme Editor `section:unload` event
         */
        onUnload() {
            this.productForm.destroy();
        },

    });



    /**
     * Load Video content on Product Swiper
     */
    const ProductVideo = (() => {
        const videos = {};

        const hosts = {
            html5: 'html5',
            youtube: 'youtube'
        };

        const attributes = {
            enableVideoLooping: 'enable-video-looping',
            videoId: 'video-id'
        };

        // Initialize Video Container
        const init = (videoContainer, sectionId) => {
            if (!videoContainer) {
                return;
            }

            const videoElement = videoContainer.querySelectorAll('iframe, video')[0];
            const mediaId = videoContainer.dataset.mediaId;

            if (!videoElement) {
                return;
            }

            videos[mediaId] = {
                mediaId: mediaId,
                sectionId: sectionId,
                host: hostFromVideoElement(videoElement),
                container: videoContainer,
                element: videoElement,
            };

            const video = videos[mediaId];
            switch (video.host) {
                case hosts.html5:
                    setupPlyrVideos();
                    break;
                case hosts.youtube:
                    setupYouTubeVideos();
                    break;
            }
        };

        // Set Up HTML 5 Players
        const setupPlyrVideos = () => {
            loadVideos(hosts.html5);
        };

        // Set Youtube embedded Videos
        const setupYouTubeVideos = () => {
            window.YT.ready(function() {
                loadVideos(hosts.youtube);
            });
        };

        // Create Video Player
        const createPlayer = (video) => {
            if (video?.player) {
                return;
            }

            const productMediaWrapper = video.container.closest(
                selectors.productMediaWrapper
            );
            const enableLooping = productMediaWrapper.dataset[attributes.enableVideoLooping];

            switch (video.host) {
                case hosts.html5:
                    video.player = new Shopify.Plyr(video.element, {
                        loop: { active: enableLooping }
                    });
                    toggleInnerFocus(selectors.productSwiperFeature);
                    break;
                case hosts.youtube:
                    const videoId = productMediaWrapper.dataset[attributes.videoId];
                    video.player = new YT.Player(video.element, {
                        videoId: videoId,
                        events: {
                            onStateChange: (event) => {
                                if (event.data === 0 && enableLooping) event.target.seekTo(0);
                            }
                        }
                    });
                    break;
            }

            productMediaWrapper.addEventListener('mediaHidden', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.pause();
                }

                if (video.host === hosts.youtube && video.player.pauseVideo) {
                    video.player.pauseVideo();
                }
            });

            productMediaWrapper.addEventListener('mediaVisible', () => {
                if (!video.player) return;

                if (video.host === hosts.html5) {
                    video.player.play();
                }

                if (video.host === hosts.youtube && video.player.playVideo) {
                    video.player.playVideo();
                }
            });
        };

        // Get host source from Video
        const hostFromVideoElement = (video) => {
            if (video.tagName === 'VIDEO') {
                return hosts.html5;
            }

            if (video.tagName === 'IFRAME') {
                if (
                    /^(https?:\/\/)?(www\.)?(youtube\.com|youtube-nocookie\.com|youtu\.?be)\/.+$/.test(
                        video.src
                    )
                ) {
                    return hosts.youtube;
                }
            }
            return null;
        };

        // Load Video Players
        const loadVideos = (host) => {
            for (const videoId in videos) {
                const video = videos.hasOwnProperty(videoId) ? videos[videoId] : null;

                if (video?.host === host) createPlayer(video);
            }
        };

        // Remove Video Players
        const removeSectionVideos = (sectionId) => {
            for (const videoId in videos) {
                const video = videos.hasOwnProperty(videoId) ? videos[videoId] : null;

                if (video?.sectionId === sectionId) {
                    if (video.player) video.player.destroy();
                    delete videos[key];
                }
            }
        };

        return {
            init: init,
            hosts: hosts,
            loadVideos: loadVideos,
            removeSectionVideos: removeSectionVideos
        };
    })();

    load('product');
});

/**
 * Slider Accessibility Helper
 */
const toggleInnerFocus = (slider) => {
    const focusableElements = ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer'];
    focusableElements.forEach((fosusableElement) => {
        slider.slides.forEach((slide, index) => {
            if (index === slider.activeIndex) {
                if (fosusableElement === '.plyr') {
                    slide.querySelector(fosusableElement)?.setAttribute('tabindex', '0');
                } else {
                    slide.querySelectorAll(fosusableElement).forEach((elem) => {
                        elem?.removeAttribute('tabindex');
                    });
                }
            } else {
                slide.querySelectorAll(fosusableElement).forEach((elem) => {
                    elem?.setAttribute('tabindex', '-1');
                });
            }
        });
    });
};
